import React, { useState, useEffect, useMemo, useRef } from "react";
import { RouteComponentProps } from "@reach/router";
import {
  Button,
  ButtonType,
  Spinner,
  Dropdown,
  DropdownToggleType,
  InfoTooltip,
} from "../Components";
import { MdAutoAwesome } from "react-icons/md";
import { CDN } from "../SingleChannel/MetricsTable/metricsTableUtils";
import { useCompanyInfo } from "../redux/company";
import {
  getCopytigerStyleGuides,
  StyleGuideOption,
  getCopytigerModelInstances,
  ModelInstanceOption,
  generateCopytigerAds,
} from "./copytigerUtils";
import { awaitJSON, CopytigerLambdaFetch } from "../utils/fetch-utils";
import { useSetError } from "../redux/modals";
import "./Generate.scss";

interface GenerateProps extends RouteComponentProps {
  onHide: () => void;
}

interface TrainingTableRow {
  id?: string;
  company: string;
  account: string;
  accountName?: string;
  campaignID: string;
  campaignName?: string;
  adGroupId: string;
  adGroupName?: string;
  adGroupCriterionKeywordText: string;
  impressions: number;
  spend: number;
  clicks: number;
}

const Generate: React.FC<GenerateProps> = ({ onHide }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const isMountedRef = useRef(true);
  const [isLoadingKeywords, setIsLoadingKeywords] = useState(false);
  const [isLoadingStyleGuides, setIsLoadingStyleGuides] = useState(false);
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [selectedStyleGuide, setSelectedStyleGuide] = useState<string>("");
  const [selectedCreativity, setSelectedCreativity] = useState<string>("0.95");

  const [models, setModels] = useState<ModelInstanceOption[]>([]);

  const [styleGuides, setStyleGuides] = useState<StyleGuideOption[]>([]);

  // Training data for Keywords column
  const [trainingData, setTrainingData] = useState<TrainingTableRow[]>([]);
  const [accountOptions, setAccountOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [campaignOptions, setCampaignOptions] = useState<Array<{ value: string; label: string }>>(
    []
  );
  const [adGroupOptions, setAdGroupOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [keywords, setKeywords] = useState<string[]>([]);

  const [selectedAccount, setSelectedAccount] = useState<string>("");
  const [selectedCampaign, setSelectedCampaign] = useState<string>("");
  const [selectedAdGroup, setSelectedAdGroup] = useState<string>("All");
  const [keywordInput, setKeywordInput] = useState<string>("");

  const temperatureOptions = [
    { value: "0.00", label: "Conservative" },
    { value: "0.95", label: "Balanced" },
    { value: "1.5", label: "Creative" },
    { value: "1.95", label: "Most Creative" },
  ];
  const GOOGLE_GEMINI_LOGO = `${CDN}/assets/img/Gemini.png`;

  const companyInfo = useCompanyInfo();
  const setError = useSetError();
  const company = companyInfo?.cid;

  // Compute account options from training data
  const computedAccountOptions = useMemo(() => {
    if (!trainingData || !trainingData.length) {
      return [];
    }

    const uniqueAccounts = new Set();
    const accountMap = new Map();

    trainingData.forEach(row => {
      if (row && row.account) {
        const accountKey = row.account;
        if (!uniqueAccounts.has(accountKey)) {
          uniqueAccounts.add(accountKey);
          const displayName = row.accountName || row.account;
          accountMap.set(accountKey, `${displayName} (${row.account})`);
        }
      }
    });

    return Array.from(accountMap.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }));
  }, [trainingData]);

  // Compute campaign options from training data (filtered by selected account)
  const computedCampaignOptions = useMemo(() => {
    if (!trainingData || !trainingData.length || !selectedAccount) {
      return [];
    }

    const uniqueCampaigns = new Set();
    const campaignMap = new Map();

    trainingData
      .filter(row => row && row.account === selectedAccount)
      .forEach(row => {
        if (row && row.campaignID) {
          const campaignKey = row.campaignID;
          if (!uniqueCampaigns.has(campaignKey)) {
            uniqueCampaigns.add(campaignKey);
            const displayName = row.campaignName || row.campaignID;
            campaignMap.set(campaignKey, `${displayName} (${row.campaignID})`);
          }
        }
      });

    return Array.from(campaignMap.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }));
  }, [trainingData, selectedAccount]);

  // Compute ad group options from training data (filtered by selected account and campaign)
  const computedAdGroupOptions = useMemo(() => {
    if (!trainingData || !trainingData.length || !selectedAccount || !selectedCampaign) {
      return [{ value: "All", label: "All" }];
    }

    const uniqueAdGroups = new Set();
    const adGroupMap = new Map();

    trainingData
      .filter(row => row && row.account === selectedAccount && row.campaignID === selectedCampaign)
      .forEach(row => {
        if (row && row.adGroupId) {
          const adGroupKey = row.adGroupId;
          if (!uniqueAdGroups.has(adGroupKey)) {
            uniqueAdGroups.add(adGroupKey);
            const displayName = row.adGroupName || row.adGroupId;
            adGroupMap.set(adGroupKey, `${displayName} (${row.adGroupId})`);
          }
        }
      });

    const sortedOptions = Array.from(adGroupMap.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }));

    return [{ value: "All", label: "All" }, ...sortedOptions];
  }, [trainingData, selectedAccount, selectedCampaign]);

  // Compute keywords from training data (filtered by all selections)
  const computedKeywords = useMemo(() => {
    if (
      !trainingData ||
      !trainingData.length ||
      !selectedAccount ||
      !selectedCampaign ||
      !selectedAdGroup
    ) {
      return [];
    }

    const keywordSet = new Set<string>();

    trainingData
      .filter(
        row =>
          row &&
          row.account === selectedAccount &&
          row.campaignID === selectedCampaign &&
          (selectedAdGroup === "All" || row.adGroupId === selectedAdGroup)
      )
      .forEach(row => {
        if (row && row.adGroupCriterionKeywordText) {
          keywordSet.add(row.adGroupCriterionKeywordText.trim());
        }
      });

    return Array.from(keywordSet).sort();
  }, [trainingData, selectedAccount, selectedCampaign, selectedAdGroup]);

  // Functions for manual keyword management
  const addKeyword = () => {
    const trimmedKeyword = keywordInput.trim();
    if (trimmedKeyword && !keywords.includes(trimmedKeyword)) {
      setKeywords(prev => [...prev, trimmedKeyword]);
      setKeywordInput("");
    }
  };

  const removeKeyword = (keywordToRemove: string) => {
    setKeywords(prev => prev.filter(keyword => keyword !== keywordToRemove));
  };

  const handleKeywordInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addKeyword();
    }
  };

  // Update computed options when dependencies change
  useEffect(() => {
    setAccountOptions(computedAccountOptions);
  }, [computedAccountOptions]);

  useEffect(() => {
    setCampaignOptions(computedCampaignOptions);
    if (selectedCampaign && !computedCampaignOptions.find(opt => opt.value === selectedCampaign)) {
      setSelectedCampaign("");
    }
  }, [computedCampaignOptions, selectedCampaign]);

  useEffect(() => {
    setAdGroupOptions(computedAdGroupOptions);
    if (selectedAdGroup && !computedAdGroupOptions.find(opt => opt.value === selectedAdGroup)) {
      setSelectedAdGroup("");
    }
  }, [computedAdGroupOptions, selectedAdGroup]);

  useEffect(() => {
    // Combine computed keywords from backend with manually added keywords
    setKeywords(prev => {
      const manualKeywords = prev.filter(keyword => !computedKeywords.includes(keyword));
      return [...computedKeywords, ...manualKeywords];
    });
  }, [computedKeywords]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleGenerateAds = async () => {
    // Validation
    if (!selectedModel) {
      if (isMountedRef.current) {
        setError({ message: "Please select a model." });
      }
      return;
    }

    if (!selectedStyleGuide) {
      if (isMountedRef.current) {
        setError({ message: "Please select a style guide." });
      }
      return;
    }

    if (keywords.length === 0) {
      if (isMountedRef.current) {
        setError({ message: "Please add at least one keyword." });
      }
      return;
    }

    if (isMountedRef.current) {
      setIsProcessing(true);
    }

    try {
      // Find the selected style guide and model to get their IDs
      const selectedStyleGuideData = styleGuides.find(sg => sg.name === selectedStyleGuide);
      const selectedModelData = models.find(m => m.id === selectedModel);

      if (!selectedStyleGuideData) {
        throw new Error("Selected style guide not found");
      }

      if (!selectedModelData) {
        throw new Error("Selected model not found");
      }

      await generateCopytigerAds(
        company,
        keywords,
        selectedStyleGuideData.id,
        selectedModelData.id,
        parseFloat(selectedCreativity)
      );
    } catch (error) {
      if (isMountedRef.current) {
        setError({
          message: "Failed to generate ads. Please check your network connection and try again.",
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsProcessing(false);
      }
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (!company) {
        return;
      }

      setIsLoadingKeywords(true);
      setIsLoadingStyleGuides(true);

      try {
        // Load style guides
        const fetchedStyleGuides = await getCopytigerStyleGuides(company);
        setStyleGuides(fetchedStyleGuides);
        setIsLoadingStyleGuides(false);

        if (fetchedStyleGuides.length > 0) {
          // Style guides loaded successfully
        }

        // Load model instances
        const fetchedModels = await getCopytigerModelInstances(company);
        setModels(fetchedModels);

        // Load training data for keywords - using last 90 days
        const endDate = new Date().toISOString().split("T")[0];
        const startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0];

        const resp = await CopytigerLambdaFetch("/getCopytigerTrainingOptions", {
          method: "GET",
          params: {
            company: company,
            startDate: startDate,
            endDate: endDate,
          },
        });

        const raw: any[] = await awaitJSON(resp);
        const trainingTableData: TrainingTableRow[] = raw.map(obj => ({
          id: obj.id || `${obj.campaignid}-${obj.adgroupid}-${obj.account}`,
          company: obj.company,
          account: obj.account,
          accountName: obj.accountname || "",
          campaignID: obj.campaignid,
          campaignName: obj.campaignname || "",
          adGroupId: obj.adgroupid,
          adGroupName: obj.adgroupname || "",
          adGroupCriterionKeywordText: obj.adgroupcriterionkeywordtext,
          impressions: Number(obj.impressions),
          spend: Number(obj.spend),
          clicks: Number(obj.clicks),
        }));

        setTrainingData(trainingTableData);
      } catch (error) {
        setError({
          message: `Failed to load data for ${company}.`,
        });
      } finally {
        setIsLoadingKeywords(false);
        setIsLoadingStyleGuides(false);
      }
    };

    loadData();
  }, [company, setError]);

  // Don't render if company info is not available
  if (!companyInfo || !company) {
    return (
      <div className="copytigerGenerateWidget">
        <div className="copytigerGenerateWidgetContainer">
          <div className="loadingState">
            <Spinner />
            <span>Loading company information...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="copytigerGenerateWidget">
      <div className="copytigerGenerateWidgetContainer">
        <div className="copytigerGenerateWidgetHeader">
          <div className="titleContainer">
            <h2 className="copytigerGenerateWidgetTitle">Generate Ads with Copy Cat</h2>
            <div className="infoContainer">
              Use Google's AI technology to streamline ideation, enable structured A/B testing, and
              enhance performance outcomes.
            </div>
          </div>
          <div className="poweredByContainer">
            <div className="poweredByText">Powered by:</div>
            <img src={GOOGLE_GEMINI_LOGO} alt="Google Gemini Logo" className="googleLogo" />
          </div>
        </div>

        <div className="copytigerGenerateWidgetBody">
          <div className="fourColumnContainer">
            <div className="column">
              <div className="columnHeader">
                <h3 className="columnTitle">
                  1. Model
                  <InfoTooltip size="sm">
                    Select which trained model to use for generating your ads. Different models are
                    optimized for different campaign types and objectives.
                  </InfoTooltip>
                </h3>
                <p className="columnDescription">
                  About models and why we have them and how they'll impact your ads
                </p>
              </div>
              <div className="columnContent">
                {models.map(model => (
                  <div
                    key={model.id}
                    className={`radioOption ${selectedModel === model.id ? "selected" : ""}`}
                  >
                    <input
                      type="radio"
                      id={model.id}
                      name="model"
                      value={model.id}
                      checked={selectedModel === model.id}
                      onChange={e => setSelectedModel(e.target.value)}
                    />
                    <label htmlFor={model.id}>
                      <strong>{model.name}</strong>
                      <span className="optionDescription">
                        {model.description || "No description available"}
                      </span>
                    </label>
                  </div>
                ))}
                {models.length === 0 && <div className="emptyState">Loading models...</div>}
              </div>
            </div>

            <div className="column">
              <div className="columnHeader">
                <h3 className="columnTitle">
                  2. Style Guide
                  <InfoTooltip size="sm">
                    Choose your brand voice and content rules to maintain consistency across all
                    generated ads.
                  </InfoTooltip>
                </h3>
                <p className="columnDescription">
                  About style guides and why we have them and how they'll impact your ads
                </p>
              </div>
              <div className="columnContent">
                {isLoadingStyleGuides ? (
                  <div className="loadingState">
                    <Spinner />
                    <span>Loading style guides...</span>
                  </div>
                ) : styleGuides.length === 0 ? (
                  <div className="emptyState">
                    <p>No style guides found.</p>
                    <p>
                      Please create a style guide in the{" "}
                      <a href={`/${company}/copytiger/style-guide`} className="styleGuideLink">
                        Style Guide tab
                      </a>{" "}
                      to get started.
                    </p>
                  </div>
                ) : (
                  styleGuides.map(styleGuide => (
                    <div
                      key={styleGuide.name}
                      className={`radioOption ${
                        selectedStyleGuide === styleGuide.name ? "selected" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        id={styleGuide.name}
                        name="styleGuide"
                        value={styleGuide.name}
                        checked={selectedStyleGuide === styleGuide.name}
                        onChange={e => setSelectedStyleGuide(e.target.value)}
                      />
                      <label htmlFor={styleGuide.name}>
                        <strong>{styleGuide.name}</strong>
                        <span className="optionDescription">
                          {styleGuide.description || "No description available"}
                        </span>
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="column">
              <div className="columnHeader">
                <h3 className="columnTitle">
                  3. Creativity
                  <InfoTooltip size="sm">
                    Control the creativity level of generated ads. Higher values produce more
                    creative and varied content.
                  </InfoTooltip>
                </h3>
                <p className="columnDescription">
                  About creativity levels and how they impact your ad generation
                </p>
              </div>
              <div className="columnContent">
                {temperatureOptions.map(option => (
                  <div
                    key={option.value}
                    className={`radioOption ${
                      selectedCreativity === option.value ? "selected" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      id={`creativity-${option.value}`}
                      name="creativity"
                      value={option.value}
                      checked={selectedCreativity === option.value}
                      onChange={e => setSelectedCreativity(e.target.value)}
                    />
                    <label htmlFor={`creativity-${option.value}`}>
                      <strong>{option.label}</strong>
                      <span className="optionDescription">
                        {option.value === "0.00" && "More predictable and safe content"}
                        {option.value === "0.95" &&
                          "Balanced approach between creativity and consistency"}
                        {option.value === "1.5" && "More creative and varied content"}
                        {option.value === "1.95" && "Highly creative and experimental content"}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="column">
              <div className="columnHeader">
                <h3 className="columnTitle">
                  4. Keywords
                  <InfoTooltip size="sm">
                    Select keywords from your campaigns or add custom ones to guide ad generation.
                  </InfoTooltip>
                </h3>
                <p className="columnDescription">
                  About keywords and how they'll impact your ad targeting and content
                </p>
              </div>
              <div className="columnContent">
                {isLoadingKeywords ? (
                  <div className="loadingState">
                    <Spinner />
                  </div>
                ) : (
                  <div className="keywordsSection">
                    <div className="accountInfo">
                      <div className="dropdown">
                        <label>Account:</label>
                        <Dropdown
                          type={DropdownToggleType.OUTLINED}
                          value={selectedAccount}
                          options={accountOptions}
                          onChange={setSelectedAccount}
                          size="sm"
                          placeholder="Select Account"
                        />
                      </div>
                      <div className="dropdown">
                        <label>Campaign:</label>
                        <Dropdown
                          type={DropdownToggleType.OUTLINED}
                          value={selectedCampaign}
                          options={campaignOptions}
                          onChange={setSelectedCampaign}
                          size="sm"
                          placeholder="Select Campaign"
                          disabled={!selectedAccount}
                        />
                      </div>
                      <div className="dropdown">
                        <label>Ad Group:</label>
                        <Dropdown
                          type={DropdownToggleType.OUTLINED}
                          value={selectedAdGroup}
                          options={adGroupOptions}
                          onChange={setSelectedAdGroup}
                          size="sm"
                          placeholder="Select Ad Group"
                          disabled={!selectedCampaign}
                        />
                      </div>
                    </div>
                    <div className="keywordTags">
                      {keywords.map((keyword, index) => (
                        <span key={index} className="tag">
                          {keyword}
                          <span
                            className="removeTag"
                            onClick={() => removeKeyword(keyword)}
                            title="Remove keyword"
                          >
                            ×
                          </span>
                        </span>
                      ))}
                      {keywords.length === 0 && (
                        <span className="emptyState">
                          Keywords will appear here when you select an account, campaign, and ad
                          group, or add them manually
                        </span>
                      )}
                    </div>
                    <div className="addKeywordsSection">
                      <input
                        type="text"
                        className="keywordInput"
                        placeholder="Type a keyword and press Enter"
                        value={keywordInput}
                        onChange={e => setKeywordInput(e.target.value)}
                        onKeyPress={handleKeywordInputKeyPress}
                      />
                      <button
                        className="addKeywordsButton"
                        onClick={addKeyword}
                        disabled={!keywordInput.trim()}
                      >
                        + Add
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="copytigerGenerateWidgetFooter">
          <Button
            className="clearButton"
            design="secondary"
            onClick={() => {
              // Reset all selections
              setSelectedModel("");
              setSelectedStyleGuide("");
              setSelectedCreativity("0.95");
              // Reset keyword selections and manual inputs
              setKeywords([]);
              setKeywordInput("");
              setSelectedAccount("");
              setSelectedCampaign("");
              setSelectedAdGroup("All");
              onHide();
            }}
            size="lg"
            type={ButtonType.OUTLINED}
          >
            Clear
          </Button>

          {isProcessing ? (
            <Spinner />
          ) : (
            <Button
              className="generateAdsButton"
              onClick={handleGenerateAds}
              size="lg"
              type={ButtonType.FILLED}
            >
              <MdAutoAwesome />
              Generate Ads
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Generate;
